import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ForwardedRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  Loader2,
  MapPin,
  X,
} from "lucide-react";
import { debounce } from "lodash";

import {
  accountVar,
  applicationPageVar,
  expertPanelVar,
  InvalidAddressType,
  invalidAddressTypeVar,
  languageVar,
  Pages,
  Sections,
  wizardNavigatorVar,
} from "@/variables/globalVar";
import { type AddressSuggestion, type Application } from "shared/apiClient";
import { cn, getText, titleCase } from "shared/lib";

import {
  getAccountQueryKey,
  useGetAddressSuggestions,
  useSelectAddress,
} from "shared/apiHooks";
import { useCouncilLocalisation } from "shared/hooks";
import {
  CaptureEventType,
  useAccount,
  useCanSelectPanel,
  useCaptureEvent,
} from "@/hooks";

export interface AddressContainerAPI {
  submitSelectedAddress: () => void;
}

export interface AddressContainerProps {
  onSelectedAddress?: (addressId: string) => void;
  onSubmitAddressCompleted?: () => void;
  onSubmitAddressFailed?: () => void;
  placeholder?: string;
  prefixIcon?: ReactNode;
  showSelectButton?: boolean;
}

export const AddressContainer = forwardRef(function AddressContainer(
  {
    onSelectedAddress,
    onSubmitAddressCompleted,
    onSubmitAddressFailed,
    placeholder,
    prefixIcon,
    showSelectButton = true,
  }: AddressContainerProps,
  ref: ForwardedRef<AddressContainerAPI>,
) {
  const queryClient = useQueryClient();

  const { captureEvent } = useCaptureEvent();

  const setAccountVar = useSetAtom(accountVar);
  const setApplicationPageVar = useSetAtom(applicationPageVar);
  const setWizardNavigatorVar = useSetAtom(wizardNavigatorVar);
  const setInvalidAddressTypeVar = useSetAtom(invalidAddressTypeVar);

  const canSelectPanel = useCanSelectPanel();
  const expertPanel = useAtomValue(expertPanelVar);
  const language = useAtomValue(languageVar);

  const { application, conversation } = useAccount();
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const [searchInput, setSearchInput] = useState("");
  const [errMessage, setErrMessage] = useState<ReactNode>("");
  const [predictionResults, setPredictionResults] = useState<
    Array<AddressSuggestion>
  >([]);
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    Array<AddressSuggestion>
  >([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");

  const texts = useMemo(
    () => [
      getText("👀 Looking up the zone..."),
      getText("🌳 Checking for overlays..."),
      getText("📐 Measuring the lot size..."),
      getText("🛰️ Finding an aerial photo..."),
    ],
    [],
  );

  const [currentText, setCurrentText] = useState<string>(texts[0]);

  const { isPending: selectAddressLoading, mutate: selectAddress } =
    useSelectAddress({
      onSuccess: (response) => {
        captureEvent(CaptureEventType.USER_ADDRESS_SUBMITTED, {
          unstructured_address: response.data?.address?.unstructured_address,
          councilCode: response.data?.council?.code,
        });

        onSubmitAddressCompleted?.();
        queryClient.invalidateQueries({ queryKey: getAccountQueryKey() });
        setApplicationPageVar(Pages.App);
      },
      onError: (error) => {
        console.error("<AddressContainer> useSelectAddress() onError()", error);
        onSubmitAddressFailed?.();
        const response = error.response;
        if (
          error.status === 412 &&
          response?.data &&
          typeof response?.data === "object" &&
          "error" in response.data
        ) {
          setInvalidAddressTypeVar(response.data.error as InvalidAddressType);
          setAccountVar((draft) => {
            if (draft && response.data && "application_data" in response.data) {
              draft.current_application = response.data
                .application_data as Application;
            }
          });
          setWizardNavigatorVar(Sections.InvalidAddress);
        } else if (
          response &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          // setErrMessage(response.data[0]);
          setWizardNavigatorVar(Sections.AddressError);
        } else {
          setWizardNavigatorVar(Sections.AddressError);
          // setErrMessage(
          //   <>
          //     <p>There was an error retrieving details about this address.</p>
          //     <p className="mt-1">
          //       Try refreshing your browser and entering the address again. If
          //       that doesn't work,{" "}
          //       <span className="text-textLink hover:text-textLinkHover underline">
          //         <a
          //           href={getLocalisationEntry("councilContactLink")}
          //           rel="noreferrer"
          //           tabIndex={0}
          //           target="_blank"
          //         >
          //           contact council
          //         </a>
          //       </span>{" "}
          //       to discuss your proposal.
          //     </p>
          //   </>,
          // );
        }
      },
    });

  const {
    isPending: addressSuggestionsLoading,
    isSuccess: addressSuggestionsSuccess,
    mutate: getAddressSuggestions,
  } = useGetAddressSuggestions({
    onError: (error) => {
      console.error(
        "<AddressContainer> useGetAddressSuggestions() onError()",
        error,
      );
    },
    onSuccess: (response) => {
      setPredictionResults(response.data?.suggestions || []);
      console.log(
        "<AddressContainer> useGetAddressSuggestions() onSuccess()",
        response.data,
      );

      captureEvent(CaptureEventType.USER_ADDRESS_SEARCH, {
        searchInput,
      });
    },
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentIndex = texts.indexOf(currentText);
      const nextIndex = (currentIndex + 1) % texts.length;
      setCurrentText(texts[nextIndex]);
    }, 2000);

    return () => clearInterval(intervalId);
  }, [currentText, texts]);

  useEffect(() => {
    const unparsedItems = localStorage.getItem("searchSuggest");
    if (unparsedItems) {
      const parsedItems = JSON.parse(unparsedItems);
      if (parsedItems) {
        setSuggestedAddresses(parsedItems);
      }
    }
  }, []);

  useEffect(() => {
    if (suggestedAddresses.length !== 0) {
      localStorage.setItem("searchSuggest", JSON.stringify(suggestedAddresses));
    }
  }, [suggestedAddresses]);

  const debouncedChangeHandler = debounce((value: string) => {
    if (value.length !== 0) {
      getAddressSuggestions({
        body: { address: value },
      });
    }
  }, 600);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    e.preventDefault();
    setSearchInput(value);
    setSelectedAddressId("");
    if (value === "") {
      setErrMessage("");
      setPredictionResults([]);
    }
    setShowAutoComplete(value !== "");
    setShowHistory(value === "");

    debouncedChangeHandler(value);
  };

  const handleSelectAddress = (addressId: string) => {
    setSelectedAddressId(addressId);
    onSelectedAddress?.(addressId);
  };

  const handleSubmitAddress = useCallback(() => {
    setErrMessage("");
    if (selectedAddressId) {
      selectAddress({
        body: {
          address_id: selectedAddressId,
          ...(canSelectPanel &&
          expertPanel?.version !== undefined &&
          expertPanel?.version !== null
            ? { panel_version: expertPanel.version }
            : {}),
          language: language?.split("-")[0] || "en",
        },
      });
      setShowAutoComplete(false);
      setShowHistory(false);
    }
  }, [canSelectPanel, expertPanel, language, selectAddress, selectedAddressId]);

  useImperativeHandle(ref, () => {
    return {
      submitSelectedAddress: () => {
        handleSubmitAddress();
      },
    };
  }, [handleSubmitAddress]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Handle keyboard events for the input field
    if (event.key === "Tab") {
      event.preventDefault();
      if (showAutoComplete) {
        setSelectedItemIndex((prevIndex) =>
          prevIndex < predictionResults?.length - 1 ? prevIndex + 1 : 0,
        );
      } else if (showHistory) {
        setSelectedItemIndex((prevIndex) =>
          prevIndex < suggestedAddresses.length - 1 ? prevIndex + 1 : 0,
        );
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      // Handle Enter key press
      if (
        predictionResults?.findIndex(
          (element) =>
            element.address.toUpperCase() === searchInput.toUpperCase(),
        ) !== -1 &&
        searchInput.length !== 0
      ) {
        handleSubmitAddress();
      }
      const selectedList = showAutoComplete
        ? predictionResults
        : suggestedAddresses;
      const selectedItem = selectedList[selectedItemIndex];
      if (selectedItemIndex >= 0 && selectedItemIndex < selectedList.length) {
        setShowAutoComplete(false);
        setShowHistory(false);

        handleSelectAddress(selectedItem.address_id);

        setSearchInput(titleCase(selectedItem.address));

        setSuggestedAddresses((prevItems) => {
          let sendBool = true;
          prevItems.forEach((item, index) => {
            if (item.address_id === selectedItem.address_id) {
              prevItems.splice(index, 1);
              sendBool = false;
            }
          });
          if (sendBool) {
            if (prevItems.length === 5) {
              prevItems.pop();
            }
            return [selectedItem, ...prevItems];
          } else {
            return prevItems;
          }
        });

        setSelectedItemIndex(-1);
      }
    }
  };

  return (
    <div className="h-[50px] w-full overflow-visible">
      <div className="flex w-full">
        <div
          tabIndex={-1}
          className={cn(
            "bg-interfaceWhite shadow-xs flex w-full cursor-text flex-col items-center justify-center overflow-hidden rounded-xl border selection:border-0",
            errMessage !== ""
              ? "border-destructiveDefault"
              : "border-interactiveBorder focus-within:border-interactiveBorderSelect",
          )}
          onBlur={(_event) => {
            if (inputRef.current) {
              inputRef.current.blur();
            }
            setSelectedItemIndex(-1);
            setShowAutoComplete(false);
            setShowHistory(false);
          }}
          onFocus={() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
            setShowAutoComplete(searchInput !== "");
            setShowHistory(searchInput === "");
          }}
        >
          <div className="flex w-full flex-row p-4 pr-2">
            {prefixIcon ? <div className="mr-2">{prefixIcon}</div> : null}
            <input
              autoComplete="off"
              className="bg-interfaceWhite text-textPrimary placeholder:text-textSubdued placeholder:focus:text-textPrimary text-bodylg grow appearance-none items-center p-0 outline-0"
              data-testid="address-search-input"
              id="address-search"
              name="address-search"
              onChange={handleChange}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder || getText("Search an address")}
              ref={inputRef}
              type="search"
              value={searchInput}
            />
            {searchInput.length > 0 && !selectAddressLoading && (
              <X
                aria-label="clear"
                className="text-textPrimary cursor-pointer pr-1"
                onClick={() => {
                  setSearchInput("");
                  setSelectedAddressId("");
                  setPredictionResults([]);
                  setErrMessage("");
                }}
                role="button"
              />
            )}
          </div>
          <div
            hidden={!(showAutoComplete || showHistory)}
            onKeyDown={handleInputKeyDown}
            className="z-10 max-h-[180px] w-full overflow-y-auto md:max-h-full"
          >
            <div>
              {searchInput?.length !== 0 && predictionResults?.length === 0 && (
                <div>
                  <div className="border-interfaceBorder text-textPrimary text-bodylg flex h-[60px] flex-row items-center border-t bg-[#f5f5f5] px-5 py-3">
                    <div className="text-textSubdued overflow-hidden text-ellipsis whitespace-nowrap">
                      {getText("No address found")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              {/*
                Address autocomplete results after typing an address in the search bar
              */}
              {searchInput !== "" &&
                predictionResults?.map((result, index) => {
                  return (
                    <div
                      key={result.address_id}
                      onClick={() => {
                        console.log(
                          "<AddressContainer> autocomplete onClick()",
                          result,
                        );
                        setSearchInput(titleCase(result.address));
                        handleSelectAddress(result.address_id);
                        setShowAutoComplete(false);
                        setShowHistory(false);

                        setSuggestedAddresses((prevItems) => {
                          let sendBool = true;
                          prevItems.forEach((item, index) => {
                            if (item.address_id === result.address_id) {
                              prevItems.splice(index, 1);
                              sendBool = false;
                            }
                          });
                          if (sendBool) {
                            if (prevItems.length === 5) {
                              prevItems.pop();
                            }
                            return [result, ...prevItems];
                          } else {
                            return prevItems;
                          }
                        });
                      }}
                      className={`text-textPrimary hover:bg-interactiveBgHover text-bodylg flex h-[60px] cursor-pointer flex-row items-center gap-3 px-5 py-3 ${selectedItemIndex === index ? "bg-interactiveBgHover" : ""} ${index === 0 ? "border-interfaceBorder border-t" : ""}`}
                    >
                      <MapPin className="text-textPrimary h-[24px] min-h-[24px] w-[24px] min-w-[24px]" />

                      <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {titleCase(result.address)}
                      </div>
                    </div>
                  );
                })}

              {/*
                Address autocomplete results pulled from localstorage cache
              */}
              {searchInput === "" &&
                suggestedAddresses.map((suggestion, index) => {
                  return (
                    <div
                      key={suggestion.address_id}
                      onClick={() => {
                        console.log(
                          "<AddressContainer> localstorage onClick()",
                          suggestion,
                        );

                        handleSelectAddress(suggestion.address_id);
                        setSearchInput(titleCase(suggestion.address));
                        setShowAutoComplete(false);
                        setShowHistory(false);
                      }}
                      className={`text-textPrimary hover:bg-interactiveBgHover focus:bg-interactiveBgHover text-bodylg flex h-[60px] cursor-pointer flex-row items-center gap-3 px-5 py-3 ${selectedItemIndex === index ? "bg-interactiveBgHover" : ""} ${index === 0 ? "border-interfaceBorder border-t" : ""}`}
                    >
                      <Clock className="text-textPrimary h-[24px] w-[24px]" />

                      <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {titleCase(suggestion.address)}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {showSelectButton ? (
          <div className="pt-4.5 pl-3">
            <button
              aria-label="Submit address"
              className="self-start"
              disabled={!selectedAddressId}
              onClick={handleSubmitAddress}
            >
              {selectAddressLoading ? (
                <Loader2 className="text-textPrimary h-[20px] w-[20px] animate-spin" />
              ) : (
                <ArrowRight
                  className={`${
                    selectedAddressId ? "text-textPrimary" : "text-textDisabled"
                  } h-[20px] w-[20px]`}
                />
              )}
            </button>
          </div>
        ) : null}
      </div>

      {selectAddressLoading && (
        <div className="text-textSubdued text-bodymd mt-3 text-left">
          {currentText}
        </div>
      )}

      {errMessage && typeof errMessage === "string" ? (
        <label
          className="text-textPrimary text-bodymd mt-2 flex flex-row items-start gap-2 text-left"
          htmlFor="address-search"
        >
          <div className="text-destructiveText mt-1">
            <AlertTriangle className="max-h-4 max-w-4" />️
          </div>
          <div
            className="text-destructiveText [&>a]:text-textLink [&>a]:hover:text-textLinkHover [&>a]:underline"
            dangerouslySetInnerHTML={{ __html: errMessage }}
          />
        </label>
      ) : null}

      {errMessage && typeof errMessage !== "string" ? (
        <label
          className="text-textPrimary text-bodymd mt-2 flex flex-row items-start gap-2 text-left"
          htmlFor="address-search"
        >
          <div className="text-destructiveText mt-1">
            <AlertTriangle className="max-h-4 max-w-4" />️
          </div>
          <div className="text-destructiveText">{errMessage}</div>
        </label>
      ) : null}
    </div>
  );
});
